import { useEffect, useState } from 'react'
import { isAxiosError } from 'axios'
import { CheckCircle2, KeyRound, Save } from 'lucide-react'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'

import {
  usePaymentGatewayCredential,
  useSavePaymentGatewayCredential,
  type PaymentGatewayConfig,
} from './paymentGatewayApi'

/**
 * This tenant's own Graviton Pay credentials.
 *
 * Deliberately a separate card from the mode selector above it, and behind a
 * separate permission. The mode only chooses simulator or live; these fields
 * decide which paymentsv3 account — that is, whose bank account — the money
 * lands in.
 *
 * The secret is write-only. The API cannot return it, so the field is always
 * blank on load and "leave blank to keep" is the literal behaviour, not a
 * convention: submitting without it sends no secret and the stored one is
 * untouched.
 *
 * Renders inside SettingsPage's form, so it deliberately uses no form element
 * of its own and saves through its own button — nested forms are invalid HTML,
 * and this card must save independently of the settings around it.
 */

const EMPTY: PaymentGatewayConfig = {
  go_base_url: '',
  base_url: '',
  client_id: '',
  profile_id: '',
  environment: 'SANDBOX',
}

const FIELDS: Array<{
  key: keyof PaymentGatewayConfig
  label: string
  hint: string
  placeholder: string
}> = [
  {
    key: 'go_base_url',
    label: 'Login host (master-gin)',
    hint: 'Host only, no path. GET /graviton/login is appended to it to fetch the token. A different service from the payments host below.',
    placeholder: 'https://login.example.com',
  },
  {
    key: 'base_url',
    label: 'Payments host (paymentsv3)',
    hint: 'Host only, no path — /v2/<environment>/payments is appended. Including /v2/test here produces /v2/test/v2/test/payments and a 404.',
    placeholder: 'https://payments.example.com',
  },
  { key: 'client_id', label: 'Client ID', hint: 'Sent as the Client_Id header on every call. An identifier, not a secret.', placeholder: '' },
  { key: 'profile_id', label: 'Profile ID', hint: 'Pins which underlying provider paymentsv3 uses. This is the field that decides where the money settles, so it stays readable.', placeholder: '' },
]

export function PaymentGatewayCredentialsCard() {
  const { data, isLoading } = usePaymentGatewayCredential()
  const save = useSavePaymentGatewayCredential()

  const [config, setConfig] = useState<PaymentGatewayConfig>(EMPTY)
  const [secret, setSecret] = useState('')
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    if (!data) return
    const c = data.config as Partial<PaymentGatewayConfig>
    setConfig({
      go_base_url: c.go_base_url ?? '',
      base_url: c.base_url ?? '',
      client_id: c.client_id ?? '',
      profile_id: c.profile_id ?? '',
      environment: c.environment ?? 'SANDBOX',
    })
  }, [data])

  // The module key counts as missing only when none is stored AND none is
  // being typed: an admin editing a URL leaves it blank on purpose, and
  // demanding it again would push them to paste the key into the browser for
  // no reason.
  const missing = [
    ...FIELDS.filter((f) => !config[f.key].trim()).map((f) => f.label),
    ...(!data?.hasSecret && !secret.trim() ? ['Module key'] : []),
  ]

  function onSave() {
    setSaved(false)
    // The key is omitted, not sent as undefined: "no secret field" is what
    // tells the API to leave the stored one alone.
    const trimmed = secret.trim()
    save.mutate(
      trimmed ? { config, secret: trimmed } : { config },
      {
        onSuccess: () => {
          setSecret('')
          setSaved(true)
        },
      },
    )
  }

  const errorMessage = save.isError
    ? isAxiosError(save.error) && save.error.response?.status === 403
      ? 'PAYMENT_GATEWAY_CONFIG_MANAGE is required.'
      : (isAxiosError(save.error) && (save.error.response?.data as { message?: string })?.message) ||
        'Could not save the credentials.'
    : null

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <KeyRound className="h-4 w-4" />
          Graviton Pay credentials
        </CardTitle>
        <CardDescription>
          This lender's own paymentsv3 account. Every tenant supplies its own —
          there is no shared platform credential to fall back on, so live
          collections stay refused until these are set.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : (
          <div className="space-y-3">
            {data?.configured ? (
              <p className="text-2xs text-muted-foreground">
                Configured{data.updatedAt ? ` · last saved ${new Date(data.updatedAt).toLocaleString()}` : ''}
                {data.hasSecret ? ' · a secret is stored' : ' · no secret stored'}
              </p>
            ) : (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-2xs text-amber-900">
                <span className="block font-medium">Not onboarded yet.</span>
                Until these are saved, raising a payment in live mode fails with
                <span className="font-mono"> graviton_pay_not_configured</span>.
              </div>
            )}

            <div className="rounded-md border border-slate-200 bg-card p-3 text-2xs text-muted-foreground">
              <span className="block font-medium text-navy-900">
                The two hosts are different services.
              </span>
              Auth is two-layered: the login host issues a token, the payments
              host takes the order. Both are host roots — the adapter appends
              its own paths. Point either at the wrong service, or paste a URL
              that already has a path on it, and raising a payment fails with a
              502 naming the URL it tried.
            </div>

            {FIELDS.map((f) => (
              <div key={f.key} className="space-y-1.5">
                <Label htmlFor={`pg-${f.key}`}>{f.label}</Label>
                <Input
                  id={`pg-${f.key}`}
                  value={config[f.key]}
                  placeholder={f.placeholder}
                  onChange={(e) => setConfig((c) => ({ ...c, [f.key]: e.target.value }))}
                />
                <p className="text-2xs text-muted-foreground">{f.hint}</p>
              </div>
            ))}

            <div className="space-y-1.5">
              <Label htmlFor="pg-environment">Environment</Label>
              <select
                id="pg-environment"
                className="h-9 w-full rounded-md border border-slate-200 bg-card px-3 text-sm"
                value={config.environment}
                onChange={(e) => setConfig((c) => ({ ...c, environment: e.target.value }))}
              >
                <option value="SANDBOX">Sandbox — calls /v2/test/…</option>
                <option value="PRODUCTION">Production — calls /v2/…</option>
              </select>
              <p className="text-2xs text-muted-foreground">
                Sandbox is the only confirmed path segment. The production
                convention was never verified against paymentsv3's own docs, so
                check one real call before a tenant takes live money.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="pg-secret">Module key</Label>
              <Input
                id="pg-secret"
                type="password"
                autoComplete="new-password"
                value={secret}
                placeholder={
                  data?.hasSecret
                    ? 'Stored — leave blank to keep it'
                    : 'Required'
                }
                onChange={(e) => setSecret(e.target.value)}
              />
              <p className="text-2xs text-muted-foreground">
                Sent as the <span className="font-mono">module_key</span> header
                on every call, so it authenticates as this lender: anyone
                holding it can raise collect requests against your paymentsv3
                account. Stored encrypted with this tenant's own key and never
                sent back — the read endpoint has no field that can carry it,
                which is why it shows blank rather than masked. Leave it blank
                to correct a URL without re-typing the key.
                {data?.hasSecret ? ' A key is currently stored.' : ''}
              </p>
            </div>

            {missing.length > 0 && (
              <p className="text-2xs text-amber-700">
                Required before saving: {missing.join(', ')}.
              </p>
            )}

            {errorMessage && (
              <Alert variant="destructive">
                <AlertTitle>Could not save</AlertTitle>
                <AlertDescription>{errorMessage}</AlertDescription>
              </Alert>
            )}

            {saved && !save.isPending && (
              <Alert variant="default">
                <CheckCircle2 className="h-4 w-4" />
                <AlertTitle>Saved</AlertTitle>
                <AlertDescription>
                  The cached login token was dropped, so the next payment call
                  signs in again with these credentials.
                </AlertDescription>
              </Alert>
            )}

            {/* type="button" and an explicit handler, not a submit: this card
                renders inside the Settings page's own form, and a nested form
                is invalid HTML. Submitting here must not also save the
                unrelated tenant settings around it. */}
            <Button
              type="button"
              onClick={onSave}
              disabled={save.isPending || missing.length > 0}
            >
              <Save className="mr-2 h-4 w-4" />
              {save.isPending ? 'Saving…' : 'Save credentials'}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
